/**
 * CSS Variables injection - single source of truth for theme colors (light only)
 */

import { brand, gray, lightTheme, warning, error, blue } from '@/shared/config/colors';

export const lightCssVariables = {
  '--color-primary': brand[500],
  '--color-primary-hover': brand[600],
  '--color-primary-active': brand[700],
  '--color-primary-light': brand[50],
  '--color-primary-lighter': brand[25],
  '--color-primary-dark': brand[950],

  '--color-text': lightTheme.text.primary,
  '--color-text-secondary': lightTheme.text.secondary,
  '--color-text-tertiary': lightTheme.text.tertiary,
  '--color-text-quaternary': gray[300],
  '--color-text-inverse': lightTheme.text.inverse,
  '--color-text-label': lightTheme.text.label,

  '--color-bg-container': lightTheme.bg.primary,
  '--color-bg-layout': lightTheme.bg.secondary,
  '--color-bg-elevated': lightTheme.bg.card,
  '--color-bg-sidebar': lightTheme.bg.sidebar,
  '--color-bg-input': lightTheme.bg.input,
  '--color-bg-primary': lightTheme.bg.primary,
  '--color-bg-tertiary': lightTheme.bg.tertiary,
  '--color-bg-card': lightTheme.bg.card,

  '--color-border': lightTheme.border.primary,
  '--color-border-secondary': lightTheme.border.secondary,
  '--color-border-focus': lightTheme.border.focus,

  '--color-success': lightTheme.status.success,
  '--color-success-light': lightTheme.status.successLight,
  '--color-warning': lightTheme.status.warning,
  '--color-warning-light': lightTheme.status.warningLight,
  '--color-warning-100': warning[100],
  '--color-warning-200': warning[200],
  '--color-warning-600': warning[600],
  '--color-warning-700': warning[700],
  '--color-error': lightTheme.status.error,
  '--color-error-light': lightTheme.status.errorLight,
  '--color-error-100': error[100],
  '--color-error-200': error[200],
  '--color-error-600': error[600],
  '--color-error-700': error[700],
  '--color-info': lightTheme.status.info,
  '--color-info-light': lightTheme.status.infoLight,
  '--color-info-100': blue[100],
  '--color-info-200': blue[200],
  '--color-info-600': blue[600],
  '--color-info-700': blue[700],
  '--color-gray-900': gray[900],
  '--color-gray-500': gray[500],

  '--shadow-xs': '0px 1px 2px rgba(16, 24, 40, 0.05)',
  '--shadow-sm': '0px 1px 3px rgba(16, 24, 40, 0.1), 0px 1px 2px rgba(16, 24, 40, 0.06)',
  '--shadow-md': '0px 4px 8px -2px rgba(16, 24, 40, 0.1), 0px 2px 4px -2px rgba(16, 24, 40, 0.06)',
  '--shadow-lg': '0px 12px 16px -4px rgba(16, 24, 40, 0.08), 0px 4px 6px -2px rgba(16, 24, 40, 0.03)',
} as const;

/**
 * Apply the (light) theme CSS variables to the document root.
 */
export const applyLightTheme = () => {
  const root = document.documentElement;

  Object.entries(lightCssVariables).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });

  root.setAttribute('data-theme', 'light');

  const metaThemeColor = document.querySelector('meta[name="theme-color"]');
  if (metaThemeColor) {
    metaThemeColor.setAttribute('content', '#ffffff');
  }
};
